import * as React from 'react';
import { Formik, FormikConsumer } from 'formik';
import Dashboard from '../../../../../models/dashboard';
import OutlinedInput from '../../../../common/outlined-input';
import * as commonStyles from '../../../../common/common.module.scss';
import PrimaryButton from '../../../../common/primary-button';
import { CircularProgress } from '@material-ui/core';
import * as Yup from 'yup';
import { withRouter } from 'react-router';
import { RouteComponentProps } from 'react-router';
import TransferList from '../../../../common/transfer-list';
import _ from 'lodash';
import PriorityTwoFailure from '../../../../announcement/priority-two-failure';
import Paths from '../../../../../models/enums/paths';
import TertiaryButton from '../../../../common/tertiary-button';
import { useRef } from 'react';
import Tag from '../../../../../models/tag';
import useDashboardManagement from '../../../../../hooks/useDashboardManagement';
import OutlinedSelect from '../../../../common/outlined-select';
import DashboardTypes from './DashboardTypes';
import NavigationWarning from '../../../navigationWarning';
import { TableauDesktopOptions } from '../../../../../models/tableau';

interface Props extends RouteComponentProps {
  /** The client ID that the Dashboard belongs to */
  clientId: number;
  /** True is loading existing dashboard */
  loading?: boolean;
  /** The dashboard to edit */
  dashboard?: Dashboard;
  /** True if creating a new dashboard. False if editing an existing dashboard. */
  isNewDashboard: boolean;
  /** URL list of tableau desktop views */
  desktopOptions: TableauDesktopOptions[];
}

const ManageDashboardSchema = Yup.object().shape({
  name: Yup.string()
    .required('Dashboard name is a required field.')
    .max(100, 'Dashboard name must be less than 100 characters'),
  description: Yup.string()
    .required('Dashboard description is a required field.')
    .max(500, 'Dashboard description must be less than 500 characters')
});

/**
 * @function DashboardManager Allows a user to view/create/edit a single dashboard's information.
 */
const DashboardManager = ({
  clientId,
  loading: loadingDashboard,
  dashboard,
  isNewDashboard,
  history,
  desktopOptions
}: Props) => {
  if (isNewDashboard) {
    //Set default dashboard values, used for Formik validation.
    dashboard = {
      reportId: null,
      name: '',
      description: '',
      assignedTags: [],
      status: null,
      reportLevel: DashboardTypes[0].key,
      contentText: ''
    };
  }
  const { createDashboard, editDashboard, loadingAssociatedData, tags, fetchAssociatedData } = useDashboardManagement();

  const [unassignedTags, setUnassignedTags] = React.useState<Tag[]>(tags);
  const [assignedTags, setAssignedTags] = React.useState<Tag[]>(dashboard.assignedTags);

  const [errorMessage, setErrorMessage] = React.useState<string>('');

  const [displayDirtyWarning, setDisplayDirtyWarning] = React.useState<boolean>(false);
  const [confirmedNavigation, setConfirmedNavigation] = React.useState<boolean>(false);
  const [blockedLocation, setBlockedLocation] = React.useState<string>('');

  const unblock = useRef(null);

  const formikRef = useRef(null);

  React.useEffect(() => {
    fetchAssociatedData(clientId);

    unblock.current = history.block((location) => {
      if (formikRef.current.dirty) {
        setDisplayDirtyWarning(true);
        setBlockedLocation(location.pathname);
        return false;
      } else {
        unblock.current();
      }
    });
  }, []);

  React.useEffect(() => {
    if (confirmedNavigation) {
      unblock.current();
      history.push(blockedLocation);
    }
  }, [confirmedNavigation]);

  React.useEffect(() => {
    if (isNewDashboard) {
      if (!loadingAssociatedData) {
        setUnassignedTags(tags);
      }
    } else {
      if (!loadingDashboard && !loadingAssociatedData) {
        if (dashboard.assignedTags === null) {
          dashboard.assignedTags = [];
        }
        setUnassignedTags(
          tags.filter((tag) => {
            let assigned = false;
            dashboard.assignedTags.forEach((assignedApp) => {
              if (tag.tagId === assignedApp.tagId) assigned = true;
            });
            return !assigned;
          })
        );
        setAssignedTags(dashboard.assignedTags);
      }
    }
  }, [loadingDashboard, loadingAssociatedData]);

  const handleCancel = (dirty: boolean) => {
    if (!dirty) {
      unblock.current();
    }
    history.push(Paths.ClientDashboards.replace(':clientId', clientId.toString()));
  };

  const mapDesktopOptions = () => {
    const options = [<option key="none" hidden value=""></option>];
    desktopOptions?.map((desktopView) =>
      options.push(
        <option key={desktopView.viewId} value={desktopView.url}>
          {desktopView.url}
        </option>
      )
    );
    return options;
  };

  return loadingDashboard || loadingAssociatedData ? (
    <div className="loadingIndicator">
      <CircularProgress />
    </div>
  ) : (
    <div>
      {errorMessage && <PriorityTwoFailure message={errorMessage} />}
      <h1>{isNewDashboard ? 'Add' : 'Edit'} Dashboard</h1>
      <Formik
        innerRef={formikRef}
        initialValues={dashboard}
        validationSchema={ManageDashboardSchema}
        onSubmit={(dashboard, { setSubmitting }) => {
          setSubmitting(true);

          if (isNewDashboard) {
            createDashboard(clientId, dashboard)
              .then((response) => {
                if (response.success) {
                  setSubmitting(false);
                  unblock.current();
                  history.push(Paths.ClientDashboards.replace(':clientId', clientId.toString()));
                }
              })
              .catch((err) => {
                setErrorMessage(err.responseJSON.message);
                window.scroll({
                  top: 0,
                  left: 0,
                  behavior: 'smooth'
                });
                setSubmitting(false);
              });
          } else {
            editDashboard(clientId, dashboard)
              .then((response) => {
                if (response.success) {
                  setSubmitting(false);
                  unblock.current();
                  history.push(Paths.ClientDashboards.replace(':clientId', clientId.toString()));
                }
              })
              .catch((err) => {
                setErrorMessage(err.responseJSON.message);
                window.scroll({
                  top: 0,
                  left: 0,
                  behavior: 'smooth'
                });
                setSubmitting(false);
              });
          }
        }}
      >
        {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting, setFieldValue, dirty }) => (
          <form>
            <FormikConsumer>
              {(formik) => (
                <NavigationWarning
                  formik={formik}
                  displayDirtyWarning={displayDirtyWarning}
                  confirmNavigationWarning={() => {
                    setConfirmedNavigation(true);
                    history.push(blockedLocation);
                  }}
                  cancleNavigationWarning={() => setDisplayDirtyWarning(false)}
                />
              )}
            </FormikConsumer>
            {isSubmitting && (
              <div className={commonStyles.overlay}>
                <div className={commonStyles.fixedCircularProgress}>
                  <CircularProgress />
                </div>
              </div>
            )}
            <fieldset className={commonStyles.fieldset}>
              <h5>Details</h5>
              <div>
                <OutlinedInput
                  id="name"
                  label="Dashboard Name"
                  name="name"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.name || ''}
                  error={errors.name && touched.name}
                  helperText={errors.name && touched.name && errors.name}
                />
              </div>
              <OutlinedInput
                id="description"
                label="Dashboard Description"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.description || ''}
                error={errors.description && touched.description}
                helperText={errors.description && touched.description && errors.description}
              />
              <OutlinedSelect
                id="reportLevel"
                title="Dashboard Type"
                value={values.reportLevel}
                handleChange={handleChange}
                handleBlur={handleBlur}
                options={DashboardTypes.map((dashboardType) => (
                  <option key={dashboardType.key} value={dashboardType.key}>
                    {dashboardType.name}
                  </option>
                ))}
              />
            </fieldset>
            <fieldset className={commonStyles.fieldset}>
              <h5>Content Column</h5>
              <OutlinedSelect
                id="contentText"
                title="Desktop"
                handleChange={handleChange}
                handleBlur={handleBlur}
                value={values.contentText}
                options={mapDesktopOptions()}
              />
            </fieldset>
            <fieldset className={`${commonStyles.fieldset}`}>
              <h5>Permission Tags</h5>
              <TransferList
                idField="tagId"
                labelField="name"
                left={unassignedTags}
                right={assignedTags}
                setLeft={setUnassignedTags}
                setRight={setAssignedTags}
                setFieldValue={setFieldValue}
                setField="assignedTags"
              />
            </fieldset>
            <div className={commonStyles.formActions}>
              <TertiaryButton text="Cancel" title="Cancel" handleClick={() => handleCancel(dirty)} />
              <span className={commonStyles.submitButton}>
                <PrimaryButton text="Save" title="Save" deactive={isSubmitting} handleClick={handleSubmit} />
              </span>
            </div>
          </form>
        )}
      </Formik>
    </div>
  );
};
export default withRouter(DashboardManager);
